import React from "react";
import { Link } from "react-router-dom";
import useConsent from "./useConsent";
import { Home } from "lucide-react";

const CookiePolicy = () => {
  const { consentState, updateCategories, jurisdiction, defaultCategories } =
    useConsent();
  const analyticsOn =
    consentState?.analytics ?? defaultCategories?.analytics ?? false;

  const handleAnalyticsToggle = () => {
    updateCategories({
      ...consentState,
      essential: true,
      analytics: !analyticsOn,
    });
  };

  return (
    <div className="min-h-screen mt-16">
      {/* Navy header */}
      <div
        className="w-full bg-navy py-10 px-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h1
            className="font-heading font-bold uppercase tracking-wide text-white"
            style={{ fontSize: "clamp(1.25rem, 4vw, 1.875rem)" }}
          >
            Cookie Policy
          </h1>
          <div className="w-20 h-1 bg-primary rounded-full mx-auto mt-2" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm mb-6 text-slate-500">
          <Link
            to="/"
            className="flex items-center gap-1 hover:text-navy transition-colors"
          >
            <Home size={14} />
            <span>Home</span>
          </Link>
          <span className="text-slate-300">›</span>
          <span className="font-heading text-primary font-semibold">
            Cookie Policy
          </span>
        </nav>

        {/* Intro card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 mb-6">
          <h2 className="font-heading text-xl font-semibold text-[#1A2A46]">
            Use of Cookies
          </h2>
          <div className="w-10 h-0.5 rounded-full bg-primary mt-1 mb-3" />
          <p className="font-body text-sm text-gray-600 leading-relaxed">
            Cookies help us remember your preferences and understand how you use
            RentGurus, so we can deliver a faster, more personalised experience
            every time you visit.
          </p>

          <div className="border-t border-gray-100 my-6" />

          <h2 className="font-heading text-xl font-semibold text-[#1A2A46]">
            Types of Cookies We Use
          </h2>
          <div className="w-10 h-0.5 bg-primary rounded-full mt-1 mb-3" />
          <p className="font-body text-sm text-gray-600 leading-relaxed">
            RentGurus uses two categories of cookies. Essential cookies keep the
            site running and cannot be disabled. Analytics cookies help us
            understand visitor behaviour so we can improve our service — you can
            turn these on or off at any time.
          </p>
        </div>

        {/* Essential card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <span className="font-heading text-base font-semibold text-[#1A2A46]">
              Essential Cookies
            </span>
            <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium">
              Always Active
            </span>
          </div>
          <div className="px-6 py-5">
            <p className="font-body text-sm text-gray-600 leading-relaxed">
              These cookies are fundamental to how RentGurus works. They secure
              your browsing session, remember the cookie choices you have made,
              and ensure that critical features like search and payment
              processing function correctly. Because they are necessary for the
              site to operate, they cannot be switched off.
            </p>
          </div>
        </div>

        {/* Analytics card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <span className="font-heading text-base font-semibold text-[#1A2A46]">
              Analytics Cookies
            </span>
            <div className="flex items-center gap-2.5">
              <label
                className="relative inline-flex items-center cursor-pointer"
                aria-label="Toggle analytics cookies"
              >
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={analyticsOn}
                  onChange={handleAnalyticsToggle}
                />
                <div
                  className="
                  w-12 h-6 rounded-full
                  bg-gray-300
                  peer-checked:bg-primary
                  peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2
                  transition-all duration-200
                  relative
                  after:content-[''] after:absolute after:top-[3px] after:left-[3px]
                  after:w-[18px] after:h-[18px] after:rounded-full
                  after:bg-white after:shadow-sm
                  after:transition-transform after:duration-200 after:ease-in-out
                  peer-checked:after:translate-x-6
                "
                />
              </label>
              <span
                className={`text-xs font-medium transition-colors duration-200 ${analyticsOn ? "text-primary" : "text-gray-400"}`}
              >
                {analyticsOn ? "On" : "Off"}
              </span>
            </div>
          </div>
          <div className="px-6 py-5">
            <p className="font-body text-sm text-gray-600 leading-relaxed">
              These cookies allow us to count visits and understand how visitors
              move around RentGurus — which pages are most popular and where
              people encounter difficulties. No personally identifiable
              information is ever collected. The insights help us make RentGurus
              faster and easier to use.
            </p>
          </div>
        </div>

        {/* Contact */}
        <div className="text-center pb-10">
          <p className="font-heading text-sm text-gray-500">
            Have questions? Please contact{" "}
            <a
              href="mailto:support@rentgurus.com"
              className="font-heading text-primary hover:underline transition-colors"
            >
              support@rentgurus.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;
